/**
 * /payments/refunds — accounts refund settlement workspace.
 *
 * Tabs: Queue (pending) / Settled / Analytics. Create from eligible cancelled
 * prepaid orders; settle with screenshot (completed) or cancel.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Banknote, Filter, ImageIcon, Receipt } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { KPICard } from '@/components/charts/KPICard';
import { BarChart } from '@/components/charts/BarChart';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useFilters, rangeISO } from '@/state/filters';
import { fmt } from '@/lib/format';
import { notify } from '@/lib/toast';
import { useAccess } from '@/features/AccessProvider';
import {
  refundImageUrl,
  useCreatePaymentRefund,
  useEligibleRefundOrders,
  usePaymentRefunds,
  useRefundAnalytics,
  useSettlePaymentRefund,
  type PaymentRefund,
  type RefundStatus,
} from '@/api/bi/refunds';

const STATUS_TONE: Record<RefundStatus, 'warning' | 'success' | 'neutral'> = {
  pending: 'warning',
  completed: 'success',
  cancelled: 'neutral',
};

function SettleDialog({
  refund,
  open,
  onOpenChange,
}: {
  refund: PaymentRefund | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const settle = useSettlePaymentRefund();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
  };

  const onFile = (f: File | null) => {
    setFile(f);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(f ? URL.createObjectURL(f) : null);
  };

  const run = async (action: 'completed' | 'cancelled') => {
    if (!refund) return;
    if (action === 'completed' && !file) {
      notify.error('Screenshot required to mark completed.');
      return;
    }
    try {
      await settle.mutateAsync({ id: refund.id, action, image: file });
      notify.success(
        action === 'completed'
          ? `Refund for order #${refund.order_id} marked completed.`
          : `Refund for order #${refund.order_id} cancelled.`,
      );
      reset();
      onOpenChange(false);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Could not settle refund.');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent>
        <div className="space-y-1 pr-6">
          <DialogTitle className="text-base font-semibold">
            Settle refund #{refund?.id}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Order #{refund?.order_id} · {fmt.npr(refund?.refund_amount ?? 0)}. Upload a
            gateway screenshot to mark completed, or cancel the request.
          </DialogDescription>
        </div>
        <div className="space-y-3 py-2">
          <label className="block text-xs text-muted-foreground">
            Screenshot
            <Input
              type="file"
              accept="image/*"
              className="mt-1"
              onChange={(e) => onFile(e.target.files?.[0] ?? null)}
            />
          </label>
          {preview ? (
            <img
              src={preview}
              alt="Refund screenshot preview"
              className="max-h-48 rounded-md border border-border object-contain"
            />
          ) : null}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            disabled={settle.isPending}
            onClick={() => void run('cancelled')}
          >
            Cancel refund
          </Button>
          <Button disabled={settle.isPending || !file} onClick={() => void run('completed')}>
            Mark completed
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const create = useCreatePaymentRefund();
  const [q, setQ] = useState('');
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const eligible = useEligibleRefundOrders({ q: debouncedQ || null, enabled: open });
  const [selected, setSelected] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [remarks, setRemarks] = useState('');
  const [refundType, setRefundType] = useState<'full' | 'partial'>('full');

  const selectedRow = eligible.data?.results.find((r) => r.order_id === selected) ?? null;

  const submit = async () => {
    if (!selected || !selectedRow) return;
    const amt =
      refundType === 'full' ? selectedRow.final_total : Number(amount);
    if (!amt || amt <= 0) {
      notify.error('Refund amount must be greater than 0.');
      return;
    }
    try {
      await create.mutateAsync({
        order_id: selected,
        refund_amount: amt,
        refund_type: refundType,
        remarks,
      });
      notify.success(`Refund created for order #${selected}.`);
      setSelected(null);
      setAmount('');
      setRemarks('');
      onOpenChange(false);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Could not create refund.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <div className="space-y-1 pr-6">
          <DialogTitle className="text-base font-semibold">Create refund</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Pick a cancelled prepaid order that has no refund yet.
          </DialogDescription>
        </div>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search order id…"
          className="mb-2"
        />
        <div className="max-h-56 overflow-y-auto rounded-md border border-border">
          {(eligible.data?.results ?? []).map((row) => (
            <button
              key={row.order_id}
              type="button"
              onClick={() => {
                setSelected(row.order_id);
                setAmount(String(row.final_total));
                setRefundType('full');
              }}
              className={`flex w-full items-center justify-between border-b border-border px-3 py-2 text-left text-xs hover:bg-muted/40 ${
                selected === row.order_id ? 'bg-primary/5' : ''
              }`}
            >
              <span>
                #{row.order_id} · {row.customer_name || '—'} · {row.payment_type}
              </span>
              <span className="font-medium">{fmt.npr(row.final_total)}</span>
            </button>
          ))}
          {eligible.isLoading ? (
            <div className="p-3 text-xs text-muted-foreground">Loading…</div>
          ) : null}
          {!eligible.isLoading && (eligible.data?.results.length ?? 0) === 0 ? (
            <div className="p-3 text-xs text-muted-foreground">No eligible orders.</div>
          ) : null}
        </div>
        {selectedRow ? (
          <div className="mt-3 space-y-2 text-xs">
            <Select
              value={refundType}
              onValueChange={(v) => setRefundType(v as 'full' | 'partial')}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Full refund</SelectItem>
                <SelectItem value="partial">Partial refund</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="number"
              value={amount}
              disabled={refundType === 'full'}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount"
            />
            <Input
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Remarks"
            />
          </div>
        ) : null}
        <div className="flex justify-end">
          <Button disabled={!selected || create.isPending} onClick={() => void submit()}>
            Create
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RefundTable({
  status,
  canManage,
  onSettle,
}: {
  status: RefundStatus | 'settled';
  canManage: boolean;
  onSettle: (r: PaymentRefund) => void;
}) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const refunds = usePaymentRefunds({
    status,
    q: debouncedQ || null,
    page,
    pageSize: 25,
  });
  const rows = refunds.data?.results ?? [];
  const pagination = refunds.data?.pagination;

  return (
    <Card>
      <CardBody className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Search order, customer, remarks…"
            className="h-8 w-64 border-0 px-1 text-xs focus-visible:ring-0"
          />
          <div className="ml-auto text-xs text-muted-foreground">
            {pagination ? `${pagination.total.toLocaleString()} refunds` : ''}
          </div>
        </div>
        {refunds.isLoading ? (
          <div className="space-y-2 px-5 py-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : refunds.isError ? (
          <div className="px-5 py-4">
            <ErrorState
              description="Could not load refunds."
              onRetry={() => void refunds.refetch()}
            />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Receipt className="h-5 w-5" />}
            title="No refunds in this view"
            description="Adjust the filter or create a refund from an eligible order."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Refund</th>
                  <th className="px-4 py-2 text-left">Order</th>
                  <th className="px-4 py-2 text-left">Customer</th>
                  <th className="px-4 py-2 text-left">Store</th>
                  <th className="px-4 py-2 text-left">Payment</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">CRM</th>
                  <th className="px-4 py-2 text-left">Proof</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const img = refundImageUrl(r.image);
                  return (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-2 font-medium">#{r.id}</td>
                      <td className="px-4 py-2">
                        {r.order_id != null ? (
                          <Link
                            to={`/orders/${r.order_id}`}
                            className="text-primary hover:underline"
                          >
                            #{r.order_id}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-2">{r.customer_name || '—'}</td>
                      <td className="px-4 py-2">{r.store?.name ?? '—'}</td>
                      <td className="px-4 py-2">
                        <div>{r.payment_type || '—'}</div>
                        {r.gateway_reference ? (
                          <div className="text-[10px] text-muted-foreground">
                            {r.gateway_reference}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-right">{fmt.npr(r.refund_amount)}</td>
                      <td className="px-4 py-2">
                        <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>
                      </td>
                      <td className="px-4 py-2">
                        {r.crm ? (
                          <Link
                            to={`/crm/tickets/${r.crm.ticket_id}`}
                            className="hover:underline"
                          >
                            {r.crm.decision}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {img ? (
                          <a href={img} target="_blank" rel="noreferrer">
                            <img
                              src={img}
                              alt="Refund screenshot"
                              className="h-10 w-8 rounded object-cover"
                            />
                          </a>
                        ) : (
                          <ImageIcon className="h-4 w-4 text-muted-foreground" />
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {r.status === 'pending' && canManage ? (
                          <Button size="sm" variant="outline" onClick={() => onSettle(r)}>
                            Settle
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">
                            {r.completed_at ? fmt.ago(r.completed_at) : '—'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pagination && pagination.total_pages > 1 ? (
          <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-2 text-xs">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              className="press rounded-md border border-border px-2 py-1 disabled:opacity-50"
            >
              Prev
            </button>
            <span>
              {pagination.page} / {pagination.total_pages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
              disabled={pagination.page >= pagination.total_pages}
              className="press rounded-md border border-border px-2 py-1 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

export function RefundsPage() {
  const { can } = useAccess();
  const canManage = can('payments.refunds', 'manage');
  const { range, storeId, timeRangeMode } = useFilters();
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const analytics = useRefundAnalytics({
    start: iso.start,
    end: iso.end,
    storeId: storeId ?? null,
  });

  const [settleTarget, setSettleTarget] = useState<PaymentRefund | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const a = analytics.data;

  return (
    <>
      <PageHeader
        eyebrow="Payments"
        title="Refund settlement"
        description="Accounts queue for gateway refunds. Approve intent in CRM; settle money here with a screenshot."
        actions={
          canManage ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Banknote className="mr-1.5 h-3.5 w-3.5" />
              Create refund
            </Button>
          ) : null
        }
      />

      <Tabs defaultValue="queue">
        <TabsList className="mb-3">
          <TabsTrigger value="queue">Queue</TabsTrigger>
          <TabsTrigger value="settled">Settled</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="queue">
          <RefundTable
            status="pending"
            canManage={canManage}
            onSettle={setSettleTarget}
          />
        </TabsContent>
        <TabsContent value="settled">
          <RefundTable
            status="settled"
            canManage={canManage}
            onSettle={setSettleTarget}
          />
        </TabsContent>
        <TabsContent value="analytics">
          {analytics.isLoading ? (
            <div className="grid gap-3 md:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          ) : analytics.isError || !a ? (
            <ErrorState
              description="Could not load refund analytics."
              onRetry={() => void analytics.refetch()}
            />
          ) : (
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-4">
                <KPICard
                  label="Pending value"
                  value={fmt.npr(a.pending.value)}
                  hint={`${a.pending.count} open`}
                />
                <KPICard
                  label="Settled value"
                  value={fmt.npr(a.settled.value)}
                  hint={`${a.settled.count} completed in range`}
                />
                <KPICard
                  label="Median settle time"
                  value={
                    a.settled.median_create_to_settle_hours != null
                      ? `${a.settled.median_create_to_settle_hours}h`
                      : '—'
                  }
                />
                <KPICard
                  label="Approved, unsettled"
                  value={fmt.npr(a.leakage.approved_unsettled_value)}
                  hint={`${a.leakage.approved_unsettled_count} CRM approvals`}
                />
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Aging (pending)</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <BarChart
                      data={[
                        { name: '0–1d', value: a.pending.aging['0_1d'] },
                        { name: '1–3d', value: a.pending.aging['1_3d'] },
                        { name: '3–7d', value: a.pending.aging['3_7d'] },
                        { name: '7d+', value: a.pending.aging['7d_plus'] },
                      ]}
                      xKey="name"
                      yKey="value"
                    />
                  </CardBody>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Settled by day</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <BarChart
                      data={a.settled.by_day.map((d) => ({
                        name: d.day ?? '—',
                        value: d.value,
                      }))}
                      xKey="name"
                      yKey="value"
                    />
                  </CardBody>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <SettleDialog
        refund={settleTarget}
        open={settleTarget != null}
        onOpenChange={(v) => {
          if (!v) setSettleTarget(null);
        }}
      />
      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
}
